import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  _handleDialogOpen = () => {
    this._triggerEl?.setAttribute('aria-expanded', 'true');
  };

  _handleDialogClose = () => {
    this._triggerEl?.setAttribute('aria-expanded', 'false');
  };

  connectedCallback() {
    super.connectedCallback();
    try {
      this._triggerEl = this.querySelector('[data-testid="cart-drawer-trigger"]');
      if (this._triggerEl && !this._triggerEl.hasAttribute('aria-expanded')) {
        this._triggerEl.setAttribute('aria-expanded', 'false');
      }

      this.addEventListener(DialogOpenEvent.eventName, this._handleDialogOpen);
      this.addEventListener(DialogCloseEvent.eventName, this._handleDialogClose);

      this.addEventListener('click', this.#handleCheckoutClick);

      const dialog = this.refs?.dialog;
      if (dialog) {
        if (!dialog.hasAttribute('role')) dialog.setAttribute('role', 'dialog');
        if (!dialog.hasAttribute('aria-modal')) dialog.setAttribute('aria-modal', 'true');
        if (!dialog.hasAttribute('aria-labelledby')) {
          const heading = this.querySelector('.cart-drawer__heading')?.id;
          if (heading) dialog.setAttribute('aria-labelledby', heading);
        }
        if (!dialog.id) {
          dialog.id = `cart-drawer-dialog-${Math.random().toString(36).slice(2, 8)}`;
        }
        if (this._triggerEl && !this._triggerEl.hasAttribute('aria-controls')) {
          this._triggerEl.setAttribute('aria-controls', dialog.id);
        }
      }
    } catch (e) {}
    
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    try {
      const header = this.querySelector('.cart-drawer__header');
      const sticky = this.querySelector('#stickyCartHeader');
      const scrollRoot = this.querySelector('.cart-drawer__content') || null;

      if (header && sticky) {
        this._stickyObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              header.classList.remove('m-stuck');
            } else {
              header.classList.add('m-stuck');
            }
          });
        }, { root: scrollRoot, threshold: 0 });

        this._stickyObserver.observe(sticky);
        this._stickyHeaderEl = header;
        this._stickyTarget = sticky;
      }
    } catch (e) {
      console.error('cart-drawer: sticky observer init failed', e);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    try {
      this.removeEventListener(DialogOpenEvent.eventName, this._handleDialogOpen);
      this.removeEventListener(DialogCloseEvent.eventName, this._handleDialogClose);
      this.removeEventListener('click', this.#handleCheckoutClick);
    } catch (e) {}
    if (this._stickyObserver) {
      try { this._stickyObserver.disconnect(); } catch (e) {}
      this._stickyObserver = null;
      this._stickyHeaderEl = null;
      this._stickyTarget = null;
    }
  }

  #handleCheckoutClick = async (/** @type {Event} */ event) =>  {
    const checkoutBtn = event.target.closest('[name="checkout"], .cart__checkout-button');
    if (!checkoutBtn) return;
    event.preventDefault();
    const emergencyRedirect = setTimeout(() => {
      window.location.href = '/checkout';
    }, 1500);

    try {
      const attributesToUpdate = {};
      const match = document.cookie.match(/(?:^|; )utag_main=([^;]*)/);
      if (match) {
        const utagMain = decodeURIComponent(match[1]);
        const vidMatch = utagMain.match(/v_id:([^$]*)/);
        if (vidMatch && vidMatch[1]) attributesToUpdate['tealium_vid'] = vidMatch[1];
      }

      if (Object.keys(attributesToUpdate).length > 0) {
        const rootRoute = window.Shopify?.routes?.root || '/';
        await fetch(`${rootRoute}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributesToUpdate })
        });
      }
    } catch (err) {
      console.error('🛡️ [Tealium] Cart update failed, bypassing...', err);
    } finally {
      clearTimeout(emergencyRedirect);
      window.location.href = '/checkout';
    }
  };

  #handleCartAdd = (/** @type {import('@theme/events').CartAddEvent} */ event) => {
    if (event.detail?.data?.skipAutoOpenCartDrawer) return;
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  open() {
    const mobileDrawerEnabled = this.classList.contains('cart-drawer--mobile-enabled');
    if (window.innerWidth < 450 && !mobileDrawerEnabled) {
      window.location.href = '/cart';
      return;
    }

    this.showDialog();

    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart:open'));
      document.dispatchEvent(new CustomEvent("free-shipping-threshold:update"));
    }, 500); 

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}